import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import { imageBase } from "../api/client";

/**
 * A record's avatar from its profile-image check: a pulsing skeleton while
 * the check is in flight, the ringed image when one is stored, otherwise a
 * letter placeholder. Shared by the Record and Capture pages.
 */
type ImageInfo = { exists: boolean; imageUrl: string | null };

export const avatarView = <M, E>(
	h: HtmlBuilder<M>,
	image: AsyncData.AsyncData<ImageInfo, E>,
	name: string,
	options: { size: string; textSize: string; alt: string },
): Html => {
	const { size, textSize, alt } = options;
	if (AsyncData.isLoading(image)) {
		return h.div(
			[h.Class("avatar avatar-placeholder")],
			[h.div([h.Class(`${size} rounded-full bg-base-300 animate-pulse`)], [])],
		);
	}
	const info = AsyncData.getOrElse(image, () => ({
		exists: false,
		imageUrl: null,
	}));
	// A null imageUrl has nothing to fetch (the `/records/:id/profile-image` fallback 404s): letter placeholder.
	if (!info.exists || info.imageUrl === null) {
		return h.div(
			[h.Class("avatar avatar-placeholder")],
			[
				h.div(
					[
						h.Class(
							`${size} bg-neutral text-neutral-content rounded-full flex items-center justify-center`,
						),
					],
					[h.span([h.Class(textSize)], [name[0]?.toUpperCase() || "?"])],
				),
			],
		);
	}
	return h.div(
		[h.Class("avatar")],
		[
			h.div(
				[
					h.Class(
						`${size} rounded-full ring-3 ring-primary ring-offset-base-100 ring-offset-1`,
					),
				],
				[h.img([h.Src(`${imageBase()}${info.imageUrl}`), h.Alt(alt)])],
			),
		],
	);
};
